import { createHash, randomBytes } from "node:crypto";
import { Whop } from "@whop/sdk";
import { openBrowser } from "../lib/open-browser";
import { oauthBaseUrl, apiBaseUrl } from "./client";
import { startCallbackServer } from "./callback-server";
import { deriveProfileName, listProfiles, upsertProfile } from "./profiles";
import {
	type AccountChoice,
	printInfo,
	selectAccount,
	startSpinner,
} from "./prompts";
import type { Identity } from "./types";

/**
 * The registered public OAuth client for the Whop CLI. The same id is created on
 * local backends by the `cli_oauth_app:setup` rake task (backend/lib/tasks/
 * cli_oauth_app.rake, run by `rake dev:setup` on backend startup) so `whop login`
 * works identically against localhost and production — keep the two in sync.
 */
export const CLI_CLIENT_ID = "app_IoPWUwtO8c2gGA";

/** Fixed local callback port — must match the redirect_uri registered on the OAuth app. */
export const CLI_REDIRECT_URI = "http://localhost:13337/callback";

import { WHOP_SCOPES, type WhopScope } from "./scopes";

export { WHOP_SCOPES, type WhopScope };

/** Scopes requested by the CLI during OAuth login. */
export const CLI_SCOPES: WhopScope[] = [...WHOP_SCOPES];

// --- PKCE ---

export function generatePKCE(): { verifier: string; challenge: string } {
	const verifier = randomBytes(32).toString("base64url");
	const challenge = createHash("sha256").update(verifier).digest("base64url");
	return { verifier, challenge };
}

export function generateState(): string {
	return randomBytes(16).toString("base64url");
}

export function generateNonce(): string {
	return randomBytes(16).toString("base64url");
}

// --- Token exchange ---

export interface OAuthTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}

async function requestToken(
	params: Record<string, string>,
): Promise<OAuthTokens> {
	const res = await fetch(`${oauthBaseUrl()}/oauth/token`, {
		method: "POST",
		headers: { "Content-Type": "application/x-www-form-urlencoded" },
		body: new URLSearchParams(params).toString(),
	});

	if (!res.ok) {
		const body = await res.text();
		throw new Error(`OAuth token request failed (${res.status}): ${body}`);
	}

	const json = (await res.json()) as {
		access_token: string;
		refresh_token: string;
		expires_in: number;
	};

	return {
		accessToken: json.access_token,
		refreshToken: json.refresh_token,
		expiresIn: json.expires_in,
	};
}

export function exchangeCode(
	code: string,
	verifier: string,
): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "authorization_code",
		client_id: CLI_CLIENT_ID,
		code,
		redirect_uri: CLI_REDIRECT_URI,
		code_verifier: verifier,
	});
}

export function refreshAccessToken(refreshToken: string): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "refresh_token",
		client_id: CLI_CLIENT_ID,
		refresh_token: refreshToken,
	});
}

// --- Authorization URL ---

export function buildAuthUrl(
	challenge: string,
	state: string,
	nonce: string,
): string {
	const base = oauthBaseUrl();
	const params = new URLSearchParams({
		client_id: CLI_CLIENT_ID,
		response_type: "code",
		redirect_uri: CLI_REDIRECT_URI,
		code_challenge: challenge,
		code_challenge_method: "S256",
		scope: CLI_SCOPES.join(" "),
		state,
		nonce,
	});
	return `${base}/oauth/authorize?${params.toString()}`;
}

// --- Full OAuth login flow ---

export class OAuthError extends Error {
	constructor(
		public readonly code: string,
		message: string,
	) {
		super(message);
		this.name = "OAuthError";
	}
}

export interface OAuthLoginResult {
	profileName: string;
	identity: Identity;
	existed: boolean;
}

/**
 * Resolves which business account an OAuth user acts on behalf of.
 *
 * OAuth tokens are user-scoped (`user_xxx`), so list the business accounts the
 * user can manage (`GET /accounts`) and pick one:
 *   - 0 business accounts → null (the user hasn't created one yet)
 *   - 1 business account  → use it
 *   - >1          → use --account-id if given, else prompt (TTY) / error (agent)
 */
/**
 * Lists the business accounts an OAuth user can manage (`GET /accounts`),
 * auto-paginating so a user with more than one page can still be resolved.
 * When `stopAtId` is given, stops as soon as it's found; otherwise the scan is
 * capped so pickers can't grow unbounded.
 */
export async function listOAuthAccounts(
	accessToken: string,
	opts: { stopAtId?: string | undefined } = {},
): Promise<AccountChoice[]> {
	const whop = new Whop({ apiKey: accessToken, baseURL: apiBaseUrl() });
	const MAX_ACCOUNTS = 1000;
	const accounts: AccountChoice[] = [];
	for await (const account of whop.companies.list({ first: 100 })) {
		accounts.push({
			id: account.id,
			title: account.title,
			route: account.route,
		});
		if (opts.stopAtId && account.id === opts.stopAtId) break;
		if (accounts.length >= MAX_ACCOUNTS) break;
	}
	return accounts;
}

async function resolveOAuthAccount(
	accessToken: string,
	opts: { agent: boolean; accountId?: string | undefined },
): Promise<Identity | null> {
	// The spinner covers only the network fetch — it must stop before any prompt,
	// otherwise it renders on top of (and underneath) the business account picker.
	const s = opts.agent ? null : startSpinner("Loading your business accounts…");
	let accounts: AccountChoice[];
	try {
		accounts = await listOAuthAccounts(accessToken, {
			stopAtId: opts.accountId,
		});
	} catch {
		s?.stop("Couldn't load your business accounts.");
		// An explicit --account-id can't be silently dropped: if the list failed to
		// load we can't verify it, so surface the error instead of logging in
		// without a business account. Without an explicit choice, stay non-fatal — API
		// commands will give a clear error rather than the login crashing.
		if (opts.accountId) {
			throw new OAuthError(
				"ACCOUNT_LOOKUP_FAILED",
				`Couldn't load your business accounts to verify "${opts.accountId}". Please try again.`,
			);
		}
		return null;
	}

	// An explicit --account-id is a hard request: verify it before the no-account
	// early return below, so an empty list errors (ACCOUNT_NOT_FOUND) rather than
	// silently logging the user in without the business account they asked for.
	if (opts.accountId) {
		const match = accounts.find((account) => account.id === opts.accountId);
		if (!match) {
			s?.stop("");
			const available = accounts.length
				? accounts.map((account) => account.id).join(", ")
				: "none — this user has no business accounts yet";
			throw new OAuthError(
				"ACCOUNT_NOT_FOUND",
				`No business account "${opts.accountId}" for this user. Available: ${available}.`,
			);
		}
		s?.stop(`Using ${match.title}.`);
		return match;
	}

	if (accounts.length === 0) {
		s?.stop("No business account found on your account.");
		return null;
	}

	if (accounts.length === 1) {
		s?.stop(`Using ${accounts[0].title}.`);
		return accounts[0];
	}

	if (opts.agent) {
		s?.stop("");
		throw new OAuthError(
			"MULTIPLE_ACCOUNTS",
			`You can manage ${accounts.length} business accounts — pass --account-id to choose one: ${accounts
				.map((account) => `${account.id} (${account.title})`)
				.join(", ")}.`,
		);
	}

	// Stop the spinner before prompting so the picker is the only live UI.
	s?.stop(`Found ${accounts.length} business accounts.`);
	return selectAccount(accounts);
}

export async function performOAuthLogin(input: {
	profile?: string | undefined;
	agent?: boolean | undefined;
	accountId?: string | undefined;
	deferAccountSelection?: boolean | undefined;
}): Promise<OAuthLoginResult> {
	const { verifier, challenge } = generatePKCE();
	const state = generateState();
	const nonce = generateNonce();
	const authUrl = buildAuthUrl(challenge, state, nonce);

	const s = startSpinner("Opening browser…");
	openBrowser(authUrl);
	s.stop("Waiting for authorization in your browser…");
	printInfo(`If your browser didn't open, visit:\n${authUrl}`);

	let code: string;
	try {
		code = await startCallbackServer(state);
	} catch (err) {
		throw new OAuthError(
			"OAUTH_CALLBACK_FAILED",
			err instanceof Error ? err.message : "OAuth callback failed.",
		);
	}

	const s2 = startSpinner("Exchanging authorization code…");
	let tokens: OAuthTokens;
	try {
		tokens = await exchangeCode(code, verifier);
		s2.stop("Authorized.");
	} catch (err) {
		s2.stop("Token exchange failed.");
		throw new OAuthError(
			"TOKEN_EXCHANGE_FAILED",
			err instanceof Error ? err.message : "Token exchange failed.",
		);
	}

	const base = oauthBaseUrl();
	let identity: Identity = {};
	let accountIdentity: Identity | null = null;
	try {
		const res = await fetch(`${base}/oauth/userinfo`, {
			headers: { Authorization: `Bearer ${tokens.accessToken}` },
		});
		if (res.ok) {
			const info = (await res.json()) as {
				sub?: string;
				name?: string;
				email?: string;
			};
			identity = { id: info.sub, title: info.name, email: info.email };
		}
	} catch {
		// Non-fatal — profile name falls back to "default"
	}

	if (!input.deferAccountSelection) {
		accountIdentity = await resolveOAuthAccount(tokens.accessToken, {
			agent: input.agent ?? false,
			accountId: input.accountId,
		});
	}

	const name = input.profile ?? deriveProfileName(identity);
	const existed = listProfiles().some((p) => p.name === name);
	const tokenExpiresAt = new Date(
		Date.now() + tokens.expiresIn * 1000,
	).toISOString();

	const profile = upsertProfile({
		name,
		method: "oauth",
		secret: tokens.refreshToken,
		identity,
		accountIdentity: accountIdentity ?? undefined,
		accessToken: tokens.accessToken,
		tokenExpiresAt,
	});

	return {
		profileName: profile.name,
		identity,
		existed,
	};
}
